"use client";

import {
	faCheck,
	faPencil,
	faRotateRight,
	faShoppingCart,
	faTriangleExclamation,
	faUserHeadset,
} from "@fortawesome/pro-solid-svg-icons";
import { CartItemTypeEnum } from "@graphql";
import type { Workpiece } from "@tosize/shapes/src/types/workpiece";
import {
	Button,
	ContactButton,
	Form,
	ProductionUnit,
	Typography,
	formatDimensions,
	useDialogContext,
	useZodForm,
} from "@tosize/ui";
import { useAction } from "next-safe-action/hooks";
import { useSearchParams, useSelectedLayoutSegments } from "next/navigation";
import { VisuallyHidden } from "radix-ui";
import { type ReactNode, useState } from "react";
import { useFormContext } from "react-hook-form";
import { P, match } from "ts-pattern";
import { addItem } from "~/app/[lang]/cart/actions/item/add";
import { useCommand } from "~/app/[lang]/command/Context";
import { ImportDialogTitle } from "~/app/[lang]/command/dialogs/import/DialogTitle";
import {
	getSimpleProductData,
	getSimpleProductsData,
} from "~/app/[lang]/command/dialogs/import/actions";
import {
	type ListImportFormType,
	defaultImportSchemaItem,
	listImportFormSchema,
} from "~/app/[lang]/command/dialogs/import/schema";
import Pie from "~/app/[lang]/command/pages/items/Progress";
import { StyledLink } from "~/components/Link";

interface ListImportFormProps {
	lang: string;
	apiUrl: string;
	groupedId: string;
	children?: ReactNode;
	messages: {
		import: string;
		fallback: string;
		successTitle: string;
		successMessage: string;
		errorMessage: string;
		loadingMessage: string;
		row: string;
		rowPlural: string;
		edit: string;
		reset: string;
		toCart: string;
		needHelp: string;
	};
}

export function ImportForm(props: ListImportFormProps) {
	const { lang, apiUrl, groupedId, children, messages } = props;

	const segments = useSelectedLayoutSegments();
	const searchParams = useSearchParams();
	const dialog = useDialogContext();

	const { activeDialog } = useCommand();

	const { executeAsync } = useAction(addItem);

	const isProductPage = !!segments.includes("product");
	const defaultGroupedProductId =
		isProductPage && segments[segments.length - 1] ? (segments[segments.length - 1] ?? "") : "";
	const defaultSimpleProductId = searchParams.get("thickness") ?? undefined;

	const { products } = getSimpleProductsData({ apiUrl, groupedProductId: defaultGroupedProductId });

	const { units } = getSimpleProductData({
		apiUrl,
		groupedProductId: defaultGroupedProductId,
		simpleProductId: defaultSimpleProductId ?? products?.at(0)?.id,
	});

	const defaultProductionUnit = units?.length
		? (units?.find((u) => u === ProductionUnit.CompletePanel) ?? ProductionUnit.M2)
		: undefined;

	const form = useZodForm({
		schema: listImportFormSchema,
		defaultValues: {
			grouped_product_id: defaultGroupedProductId,
			simple_product_id: defaultSimpleProductId ?? products?.at(0)?.id,
			production_type: defaultProductionUnit,
			items: match(activeDialog)
				.with("importProducts_list", () => [defaultImportSchemaItem])
				.otherwise(() => []),
		},
	});

	const { reset } = form;

	const [data, setData] = useState<ListImportFormType["items"]>([]);
	const [importing, setImporting] = useState(false);
	const [imported, setImported] = useState<Record<number, { data: unknown } | { error: Error }>>();
	const importDone =
		data.length && imported !== undefined ? Object.keys(imported).length === data.length : false;
	const [importSuccess, importError] = [
		Object.values(imported ?? {}).filter((item) => "data" in item),
		Object.values(imported ?? {}).filter((item) => "error" in item),
	];

	function edit() {
		setImported(undefined);
		setImporting(false);
		setData([]);
		dialog?.setForceOpen(false);
	}

	function resetForm() {
		reset();
		setImported(undefined);
		setImporting(false);
		setData([]);
		dialog?.setForceOpen(false);
	}

	return (
		<Form
			form={form}
			wrapperClassName="flex min-h-full flex-col"
			className="relative flex min-h-full grow flex-col gap-4"
			onSubmit={async (values) => {
				dialog?.setForceOpen(true);
				setImporting(true);
				setData(values.items);

				for (const [index, item] of values.items.entries()) {
					const finishes = { side: {} };

					if (item.finishesEnabled && item.finishes !== undefined) {
						finishes.side = item.finishes.sides.reduce(
							(acc, side) => {
								const sideName = match(item.finishes!.type)
									.with("afronden_up", "afronden_down", "afronden_both", () => "afronden_side")
									.with(
										"afschuinen_up",
										"afschuinen_down",
										"afschuinen_both",
										() => "afschuinen_side",
									)
									.with("matching", "custom", () => "absband_side")
									.exhaustive();

								const optionParameters = match(item)
									.with({ finishes: { radius: P.number } }, (f) => ({ radius: f.finishes.radius }))
									.with({ finishes: { depth: P.number } }, (f) => ({ depth: f.finishes.depth }))
									.with({ finishes: { thickness: P.number } }, (f) => ({
										thickness: f.finishes.thickness,
									}))
									.with({ finishes: { sku: P.string } }, (f) => ({ sku: f.finishes.sku }))
									.otherwise(() => ({}));

								acc[side] = {
									[sideName]: [
										{ option: item.finishes!.type, option_parameters: optionParameters },
									],
								};
								return acc;
							},
							{} as Workpiece["finishes"]["side"],
						);
					}

					await executeAsync({
						lang,
						cartItem: {
							id: values.simple_product_id,
							quantity: item.quantity ? item.quantity : 1,
							type:
								values.production_type === ProductionUnit.CompletePanel
									? CartItemTypeEnum.Child
									: CartItemTypeEnum.Default,
							data: JSON.stringify({
								type: "Wood",
								shape: "plate_rectangle_custom",
								label: item.label,
								shape_dimensions: {
									length: Math.round(item.length ?? 0),
									width: Math.round(item.width ?? 0),
								},
								finishes,
								accepted_condition: true,
								version: 7,
								child: true,
							} as Workpiece),
						},
					})
						.then((result) =>
							setImported((prev) => ({
								...prev,
								[index + 1]:
									result instanceof Error
										? { error: result }
										: result?.serverError
											? { error: new Error(result.serverError) }
											: { data: result },
							})),
						)
						.catch((error) => {
							if (error instanceof Error) {
								setImported((prev) => ({ ...prev, [index + 1]: { error } }));
							}
						});
				}

				setImporting(false);
				dialog?.setForceOpen(false);
			}}
		>
			<ImportDialogTitle
				apiUrl={apiUrl}
				groupedId={groupedId}
				messages={{ import: messages.import, fallback: messages.fallback }}
			/>

			{importDone ? (
				<div className="flex grow flex-col">
					<Typography variant="paragraph-bold">
						{messages.successTitle} ({Object.keys(imported ?? {}).length}/{data.length})
					</Typography>

					<div className="my-2 h-px w-full bg-shades" />

					<Typography starticon={faCheck} iconColor="success">
						{importSuccess.length}{" "}
						{importSuccess.length > 1
							? messages.rowPlural.toLocaleLowerCase()
							: messages.row.toLocaleLowerCase()}{" "}
						{messages.successMessage}
					</Typography>

					{!!importError.length && (
						<Typography starticon={faTriangleExclamation} iconColor="warning">
							{importError.length}{" "}
							{importError.length > 1
								? messages.rowPlural.toLocaleLowerCase()
								: messages.row.toLocaleLowerCase()}{" "}
							{messages.errorMessage}
						</Typography>
					)}

					<ul className="mt-1 ml-11 list-outside list-disc text-[13px] text-shades-dark">
						{Object.entries(imported ?? {}).map(([position, item], index) => {
							const dataItem = data[Number(position) - 1];
							const label = dataItem?.label;
							const dimensions = formatDimensions({
								lang,
								values: [Number(dataItem?.length ?? 0), Number(dataItem?.width ?? 0)],
								digits: { min: 0, max: 0 },
								unit: "millimeter",
							});

							if ("error" in item) {
								return (
									<li key={index}>
										<span>
											{messages.row}{" "}
											<span className="font-mono">
												{Number(position) < 10 ? `0${position}` : position}
											</span>
											{` - ${dimensions} ${label ? `(${label})` : ""}`}
										</span>
										: {item.error.message}
									</li>
								);
							}
						})}
					</ul>

					<Button onClick={() => edit()} starticon={faPencil} className="mt-4 self-start">
						{messages.edit}
					</Button>

					<div className="-mx-4 sticky bottom-0 mt-auto grid grid-cols-1 xs:grid-cols-2 gap-1 bg-shades-light p-4 xs:last:*:col-span-2 sm:flex sm:justify-end sm:gap-4 sm:[&>*:nth-child(2)]:ml-auto">
						<ContactButton variant="outlined" icon={faUserHeadset}>
							{messages.needHelp}
						</ContactButton>

						<Button onClick={() => resetForm()} starticon={faRotateRight}>
							{messages.reset}
						</Button>

						<StyledLink href="/cart" variant="contained" color="success" starticon={faShoppingCart}>
							{messages.toCart}
						</StyledLink>
					</div>
				</div>
			) : importing ? (
				<div className="flex grow flex-col">
					<div className="mb-4 flex h-full flex-col items-center justify-center rounded-xl border border-shades border-dashed p-4">
						<Pie
							percentage={(Object.keys(imported ?? {}).length / data.length) * 100}
							colour="#9AC8DB"
						/>
						<Typography>
							{messages.loadingMessage}... ({Object.keys(imported ?? {}).length}/{data.length})
						</Typography>
					</div>
				</div>
			) : null}

			{importDone || importing ? <VisuallyHidden.Root>{children}</VisuallyHidden.Root> : children}
		</Form>
	);
}

interface ShowFormFieldProps {
	name: string;
	values: unknown[] | unknown;
	inversed?: boolean;
	children: ReactNode;
}

export function ShowFormField({ name, values, inversed, children }: ShowFormFieldProps) {
	const { watch } = useFormContext();
	const current = watch(name);

	if (inversed) {
		if ((Array.isArray(values) && values.includes(current)) || values === current) {
			return null;
		}

		return children;
	}

	if ((Array.isArray(values) && values.includes(current)) || values === current) {
		return children;
	}

	return null;
}
